/**
 * One entity-type card for the demo Case section. Mirrors the Research browse
 * card (`features/search/components/EntityGroupCard`) — colored type icon,
 * count, divided rows with a dot bullet — so the demo matches the research page.
 * Adapted for the sandbox: no navigation; rows expand inline to show the
 * entity's full properties, and "Show all" toggles the list in place.
 */
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { ENTITY_ICON_COMPONENTS, LABEL_COLORS } from '@/components/neo4j/cytoscape/entity-icons'
import { Card } from '@/components/ui/card'
import type { PlaygroundEntity } from '@/services/api/playground'

const PREVIEW_COUNT = 6

/** Display name: backend-resolved `display_name` (respects the schema's
 *  is_display_name marker) → name → title → id. */
export function entityName(e: PlaygroundEntity): string {
  return e.display_name || (e.properties?.name as string) || (e.properties?.title as string) || e.id
}

function propEntries(props: Record<string, unknown> | undefined): [string, string][] {
  if (!props) return []
  return Object.entries(props)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)])
}

export function DemoEntityGroupCard({
  type,
  entities,
}: {
  type: string
  entities: PlaygroundEntity[]
}) {
  const [showAll, setShowAll] = useState(false)
  const color = LABEL_COLORS[type] ?? LABEL_COLORS.Default
  const Icon = ENTITY_ICON_COMPONENTS[type] ?? ENTITY_ICON_COMPONENTS.Default
  const visible = showAll ? entities : entities.slice(0, PREVIEW_COUNT)
  const moreCount = entities.length - visible.length

  return (
    <Card className="flex flex-col overflow-hidden p-0">
      <div className="flex w-full items-center gap-3 border-b border-border/60 px-3.5 py-3">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
          style={{ backgroundColor: `${color.fill}1a`, color: color.fill }}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
          {type}
        </span>
        <span className="shrink-0 font-mono text-[12px] tabular-nums text-muted-foreground">
          {entities.length.toLocaleString()}
        </span>
      </div>

      <ul className="divide-y divide-border/40">
        {visible.map(e => (
          <EntityRow key={e.id} entity={e} color={color.fill} />
        ))}
      </ul>

      {entities.length > PREVIEW_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="border-t border-border/40 bg-muted/10 px-3.5 py-2 text-left text-[11px] font-medium text-muted-foreground/80 transition-colors hover:bg-muted/30 hover:text-foreground"
        >
          {showAll ? (
            'Show less'
          ) : (
            <>
              Show all {entities.length.toLocaleString()}
              <span className="ml-1 text-muted-foreground/50">+{moreCount.toLocaleString()}</span>
            </>
          )}
        </button>
      )}
    </Card>
  )
}

function EntityRow({ entity, color }: { entity: PlaygroundEntity; color: string }) {
  const [open, setOpen] = useState(false)
  const name = entityName(entity)
  const entries = propEntries(entity.properties)
  const subtitle = entries
    .filter(([, v]) => v !== name)
    .slice(0, 2)
    .map(([k, v]) => `${k}: ${v}`)
    .join(' · ')
  const hasProps = entries.length > 0

  return (
    <li>
      <button
        type="button"
        onClick={() => hasProps && setOpen(v => !v)}
        disabled={!hasProps}
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-muted/30 disabled:cursor-default disabled:hover:bg-transparent md:py-1.5"
      >
        <span
          className="h-1 w-1 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] text-foreground">{name}</div>
          {subtitle && (
            <div className="truncate text-[10.5px] text-muted-foreground/80">{subtitle}</div>
          )}
        </div>
        {hasProps &&
          (open ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground/50" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/50" />
          ))}
      </button>
      {open && hasProps && (
        <dl className="space-y-0.5 border-t border-border/30 bg-muted/10 px-3.5 py-2 pl-[2.1rem]">
          {entries.map(([k, v]) => (
            <div key={k} className="flex gap-1.5 text-[10.5px] leading-snug">
              <dt className="shrink-0 text-muted-foreground">{k}</dt>
              <dd className="break-all text-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  )
}
